/**
 * ConfirmarFondeoPage — FiadoSA confirms receipt of funding sent by UNE-Credito.
 * Left: sent instructions pending confirmation + confirmed history
 * Right: credit detail for selected instruction
 */

import { useState, useMemo } from 'react';
import { RefreshCw, CheckCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES, UNE_PERM } from '@/constants';
import { Can } from '@/components/auth/Can';
import { useAuth } from '@/hooks/useAuth';
import { FundingCreditsTable } from '@/features/funding/components/FundingCreditsTable';
import {
  getSentFunding,
  confirmFunding,
  getFundingHistory,
} from '@/services/credit-payment.service';

// ─── Constants ──────────────────────────────────────────────────────────────

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Fondeo', path: ROUTES.FONDEO },
  { label: 'Confirmar', path: ROUTES.CONFIRMAR_FONDEO },
];

function formatDateShort(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
}

interface CreditItem {
  credit_id: string;
  directory_id: string;
  amount_mxn: number;
  disbursed_at?: string;
  phone_number?: string | null;
  credit_level?: string | null;
  /** Pendiente backend — ver PENDIENTES.md item 004. */
  full_name?: string | null;
}

interface FundingInstruction {
  funding_id: string;
  partner_id: string;
  total_credits_count: number;
  total_amount_mxn: number;
  status: string;
  bank_reference?: string;
  sent_by?: string;
  sent_at?: string;
  confirmed_at?: string;
  created_at: string;
  credits_detail?: CreditItem[];
}

// ─── Left: Sent Instructions (pending FiadoSA confirmation) ────────────────

function SentList({ items, selectedId, onSelect, onConfirm, isConfirming }: {
  items: FundingInstruction[];
  selectedId: string | null;
  onSelect: (item: FundingInstruction) => void;
  onConfirm: (item: FundingInstruction) => void;
  isConfirming: boolean;
}) {
  if (items.length === 0) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-sm text-muted-foreground">Sin fondeos pendientes de confirmar</p>
        <p className="mt-1 text-xs text-muted-foreground">UNE-Credito aun no ha registrado envios</p>
      </div>
    );
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
          <th className="px-3 py-1.5 font-medium">Fecha</th>
          <th className="px-2 py-1.5 text-right font-medium">Monto</th>
          <th className="px-2 py-1.5 font-medium">Referencia</th>
          <th className="px-2 py-1.5 w-10" />
        </tr>
      </thead>
      <tbody>
        {items.map((item) => {
          const selected = item.funding_id === selectedId;
          const date = (item.sent_at ?? item.created_at).split('T')[0] ?? '';
          return (
            <tr
              key={item.funding_id}
              onClick={() => onSelect(item)}
              className={`cursor-pointer border-b border-border/20 transition-colors ${selected ? 'bg-primary/5' : 'hover:bg-muted/40'}`}
              style={{ height: 36 }}
            >
              <td className="px-3 py-0 text-xs text-foreground">{formatDateShort(date)}</td>
              <td className="px-2 py-0 text-right tabular-nums text-xs font-medium text-foreground">
                {formatCurrency(item.total_amount_mxn, 'MXN', 'es-MX')}
              </td>
              <td className="px-2 py-0 text-xs text-muted-foreground font-mono">
                {item.bank_reference ? item.bank_reference.slice(0, 20) : '-'}
              </td>
              <td className="px-2 py-0">
                <Can permission={UNE_PERM.FUNDING_CONFIRM}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onConfirm(item); }}
                    disabled={isConfirming}
                    className="rounded p-1 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors disabled:opacity-30"
                    title="Confirmar recepcion"
                  >
                    <CheckCircle size={16} />
                  </button>
                </Can>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ─── Left: Confirmed History ───────────────────────────────────────────────

interface HistoryItem {
  date: string;
  total_amount_mxn: number;
  credits_count: number;
  status: string;
  bank_reference?: string;
  funding_id?: string;
  instructions?: CreditItem[];
}

function HistoryList({ items, selectedId, onSelect }: {
  items: HistoryItem[];
  selectedId: string | null;
  onSelect: (item: HistoryItem) => void;
}) {
  if (items.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">Sin historial</p>;
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
          <th className="px-3 py-1.5 font-medium">Fecha</th>
          <th className="px-2 py-1.5 text-center font-medium">Cred</th>
          <th className="px-2 py-1.5 text-right font-medium">Monto</th>
          <th className="px-2 py-1.5 font-medium">Referencia</th>
          <th className="px-2 py-1.5 text-center font-medium">Estado</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => {
          const id = item.funding_id ?? item.date;
          const selected = id === selectedId;
          const statusVariant = item.status === 'CONFIRMED' ? 'success' as const : 'warning' as const;
          const statusLabel = item.status === 'CONFIRMED' ? 'Confirmado' : 'Pendiente';
          return (
            <tr
              key={id}
              onClick={() => onSelect(item)}
              className={`cursor-pointer border-b border-border/20 transition-colors ${selected ? 'bg-primary/5' : 'hover:bg-muted/40'}`}
              style={{ height: 32 }}
            >
              <td className="px-3 py-0 text-xs text-foreground">{formatDateShort(item.date)}</td>
              <td className="px-2 py-0 text-center tabular-nums text-xs text-foreground">{item.credits_count ?? '-'}</td>
              <td className="px-2 py-0 text-right tabular-nums text-xs font-medium text-foreground">
                {formatCurrency(item.total_amount_mxn, 'MXN', 'es-MX')}
              </td>
              <td className="px-2 py-0 text-xs text-muted-foreground font-mono">
                {item.bank_reference ? item.bank_reference.slice(0, 15) : '-'}
              </td>
              <td className="px-2 py-0 text-center">
                <Badge variant={statusVariant} className="text-[9px]">{statusLabel}</Badge>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ─── Right: Credit Detail ──────────────────────────────────────────────────

function CreditDetailPanel({ credits, title, info }: { credits: CreditItem[]; title: string; info?: { bank_reference?: string | undefined; sent_by?: string | undefined } | undefined }) {
  if (credits.length === 0) {
    return <p className="py-12 text-center text-sm text-muted-foreground">Selecciona un fondeo para ver el detalle</p>;
  }

  return (
    <FundingCreditsTable credits={credits} title={title} info={info} />
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

type Selection =
  | { type: 'sent'; item: FundingInstruction }
  | { type: 'history'; item: HistoryItem }
  | { type: 'none' };

export function ConfirmarFondeoPage() {
  const { user } = useAuth();

  const { data: sentData, refetch: refetchSent } = useQuery({
    queryKey: ['funding', 'sent'],
    queryFn: () => getSentFunding(),
  });

  const { data: historyData, refetch: refetchHistory } = useQuery({
    queryKey: ['funding', 'history-confirmed'],
    queryFn: () => getFundingHistory(90),
  });

  const sentInstructions: FundingInstruction[] = sentData?.data ?? [];
  const history: HistoryItem[] = (historyData?.data ?? []).filter((h: HistoryItem) => h.status === 'CONFIRMED');

  const [selection, setSelection] = useState<Selection>({ type: 'none' });
  const [isConfirming, setIsConfirming] = useState(false);

  const detailCredits: CreditItem[] = useMemo(() => {
    if (selection.type === 'sent') {
      const d = selection.item.credits_detail;
      return d && Array.isArray(d) ? d.filter(c => c && c.credit_id) : [];
    }
    if (selection.type === 'history') {
      const d = selection.item.instructions;
      return d && Array.isArray(d) ? d.filter((c: any) => c && c.credit_id) : [];
    }
    return [];
  }, [selection]);

  const detailTitle = selection.type === 'sent'
    ? 'Fondeo pendiente de confirmar'
    : selection.type === 'history'
    ? `Fondeo confirmado`
    : '';

  const detailInfo = selection.type === 'sent'
    ? { bank_reference: selection.item.bank_reference, sent_by: selection.item.sent_by }
    : selection.type === 'history'
    ? { bank_reference: selection.item.bank_reference }
    : undefined;

  const handleConfirm = async (item: FundingInstruction) => {
    setIsConfirming(true);
    try {
      await confirmFunding(item.funding_id, {
        bankReference: item.bank_reference ?? '',
        confirmedBy: user?.email ?? 'admin',
      });
      await Promise.all([refetchSent(), refetchHistory()]);
      setSelection({ type: 'none' });
    } catch (error) {
      console.error('Error confirming funding', error);
    } finally {
      setIsConfirming(false);
    }
  };

  const refetchAll = () => { void refetchSent(); void refetchHistory(); };

  return (
    <div>
      <PageHeader
        title="Confirmar Desembolsos"
        helpKey="confirmar-desembolsos"
        subtitle="Confirmar recepcion de transferencias enviadas por UNE-Credito"
        breadcrumbs={BREADCRUMBS}
        actions={<Button onClick={refetchAll}><RefreshCw size={16} /> Actualizar</Button>}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[500px_1fr]">
        {/* ── Left Panel ──────────────────────────────────── */}
        <div className="space-y-4">
          <div className={`rounded-lg border-2 overflow-hidden ${
            sentInstructions.length > 0
              ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20'
              : 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
          }`}>
            <div className={`border-b px-3 py-2 ${
              sentInstructions.length > 0
                ? 'border-amber-200 dark:border-amber-800 bg-amber-100/50 dark:bg-amber-900/30'
                : 'border-green-200 dark:border-green-800 bg-green-100/50 dark:bg-green-900/30'
            }`}>
              <h3 className={`text-xs font-semibold uppercase tracking-wider ${
                sentInstructions.length > 0
                  ? 'text-amber-700 dark:text-amber-400'
                  : 'text-green-700 dark:text-green-400'
              }`}>
                {sentInstructions.length > 0 ? `Por confirmar (${sentInstructions.length})` : 'Todo confirmado ✓'}
              </h3>
            </div>
            <SentList
              items={sentInstructions}
              selectedId={selection.type === 'sent' ? selection.item.funding_id : null}
              onSelect={(item) => setSelection({ type: 'sent', item })}
              onConfirm={handleConfirm}
              isConfirming={isConfirming}
            />
          </div>

          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="border-b border-border bg-muted/30 px-3 py-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Confirmados</h3>
            </div>
            <HistoryList
              items={history}
              selectedId={selection.type === 'history' ? (selection.item.funding_id ?? selection.item.date) : null}
              onSelect={(item) => setSelection({ type: 'history', item })}
            />
          </div>
        </div>

        {/* ── Right Panel ─────────────────────────────────── */}
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <CreditDetailPanel credits={detailCredits} title={detailTitle} info={detailInfo} />
        </div>
      </div>
    </div>
  );
}
